import { MoreHorizOutlined } from "@mui/icons-material";
import { Button, IconButton, Paper, Stack, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";

export function MobileActionBar({
  secondaryIcon,
  secondaryLabel,
  secondaryDisplayLabel,
  onSecondary,
  additionalIcon,
  additionalLabel,
  additionalDisplayLabel,
  onAdditional,
  additionalDisabled = false,
  primaryLabel,
  onPrimary,
  primaryDisabled = false,
  onMore
}: {
  secondaryIcon?: ReactNode;
  secondaryLabel?: string;
  secondaryDisplayLabel?: string;
  onSecondary?: () => void;
  additionalIcon?: ReactNode;
  additionalLabel?: string;
  additionalDisplayLabel?: string;
  onAdditional?: () => void;
  additionalDisabled?: boolean;
  primaryLabel: string;
  onPrimary?: () => void;
  primaryDisabled?: boolean;
  onMore?: (anchor: HTMLElement) => void;
}) {
  const { t } = useTranslation();
  return (
    <Paper elevation={8} sx={{
      display: { xs: "grid", sm: "none" }, position: "fixed",
      zIndex: (theme) => theme.zIndex.appBar, left: 0, right: 0, bottom: 0,
      gridTemplateColumns: secondaryIcon && additionalIcon
        ? "58px 58px minmax(0, 1fr) 48px"
        : secondaryIcon || additionalIcon
          ? "58px minmax(0, 1fr) 48px"
          : "minmax(0, 1fr) 48px", gap: .75,
      alignItems: "center", p: 1,
      pb: "calc(8px + env(safe-area-inset-bottom))",
      borderTop: 1, borderColor: "divider"
    }}>
      {secondaryIcon && (
        <IconButton aria-label={secondaryLabel ?? t("common.secondaryAction")}
          color="primary"
          disabled={!onSecondary} onClick={onSecondary}
          sx={{ width: 58, height: 56, border: 1,
            borderColor: onSecondary ? "primary.main" : "divider",
            bgcolor: (theme) => onSecondary
              ? alpha(theme.palette.primary.main, .08) : "transparent",
            borderRadius: 1.5 }}>
          <Stack alignItems="center" spacing={.1}>
            {secondaryIcon}
            <Typography variant="caption" sx={{ fontSize: 8, lineHeight: 1 }}>
              {secondaryDisplayLabel ?? secondaryLabel}
            </Typography>
          </Stack>
        </IconButton>
      )}
      {additionalIcon && (
        <IconButton aria-label={additionalLabel ?? t("common.additionalAction")}
          color="primary"
          disabled={!onAdditional || additionalDisabled} onClick={onAdditional}
          sx={{ width: 58, height: 56, border: 1,
            borderColor: onAdditional && !additionalDisabled
              ? "primary.main" : "divider",
            bgcolor: (theme) => onAdditional && !additionalDisabled
              ? alpha(theme.palette.primary.main, .08) : "transparent",
            borderRadius: 1.5 }}>
          <Stack alignItems="center" spacing={.1}>
            {additionalIcon}
            <Typography variant="caption" sx={{ fontSize: 8, lineHeight: 1 }}>
              {additionalDisplayLabel ?? additionalLabel}
            </Typography>
          </Stack>
        </IconButton>
      )}
      <Button variant="contained" size="large" disabled={primaryDisabled || !onPrimary}
        onClick={onPrimary} sx={{ minWidth: 0, minHeight: 56,
          px: 1, fontSize: "0.82rem", lineHeight: 1.2,
          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {primaryLabel}
      </Button>
      <IconButton aria-label="Autres actions" disabled={!onMore}
        onClick={(event) => onMore?.(event.currentTarget)}
        sx={{ border: 1, borderColor: "divider", borderRadius: 1.5 }}>
        <MoreHorizOutlined />
      </IconButton>
    </Paper>
  );
}
